import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Menu, MenuProps } from 'primereact/menu';

/**
 * Custom Menu component that automatically closes when user scrolls
 * This wraps PrimeReact Menu and adds scroll event listener
 */
const CustomMenu = forwardRef<Menu, MenuProps>((props, ref) => {
  const menuRef = useRef<Menu>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Forward ref to parent component
  useImperativeHandle(ref, () => menuRef.current as Menu);

  useEffect(() => {
    if (!isVisible) return;

    const handleScroll = () => {
      // Close menu when scroll detected if menu is visible
      if (isVisible) {
        try {
          // Trigger a click on document to close the menu naturally
          // This works because PrimeReact Menu listens to document clicks to auto-close
          const overlayMenu = document.querySelector('.p-menu.p-component.p-menu-overlay');
          if (overlayMenu) {
            // Simulate a click outside the menu
            document.body.click();
          }
        } catch (e) {
          console.error('Error closing menu on scroll:', e);
        }
      }
    };

    // Find all scrollable elements (elements that can scroll)
    const scrollableElements: Element[] = [];
    const allElements = document.querySelectorAll('*');

    allElements.forEach((element) => {
      const style = window.getComputedStyle(element);
      const isScrollable =
        style.overflow === 'auto' ||
        style.overflow === 'scroll' ||
        style.overflowY === 'auto' ||
        style.overflowY === 'scroll' ||
        style.overflowX === 'auto' ||
        style.overflowX === 'scroll';

      if (isScrollable) {
        scrollableElements.push(element);
      }
    });

    // Add scroll listeners to all scrollable elements
    scrollableElements.forEach((element) => {
      element.addEventListener('scroll', handleScroll);
    });

    // Also listen to window scroll
    window.addEventListener('scroll', handleScroll);

    // Cleanup
    return () => {
      scrollableElements.forEach((element) => {
        element.removeEventListener('scroll', handleScroll);
      });
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isVisible]);

  // Track when menu is shown
  const handleShow = (e: any) => {
    setIsVisible(true);
    if (props.onShow) {
      props.onShow(e);
    }
  };

  // Track when menu is hidden
  const handleHide = (e: any) => {
    setIsVisible(false);
    if (props.onHide) {
      props.onHide(e);
    }
  };

  return <Menu ref={menuRef} {...props} onShow={handleShow} onHide={handleHide} />;
});

CustomMenu.displayName = 'CustomMenu';

export default CustomMenu;
